import React, { useState } from 'react';
import {
  X,
  Radio,
  RefreshCw,
  Clock,
  Calendar,
  Save,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Activity,
  CreditCard,
  UserCheck,
} from 'lucide-react';
import { Siswa, KehadiranSiswa, RfidTapLog } from '../../types/index.js';

interface SiswaAttendanceModalProps {
  isOpen: boolean;
  siswa: Siswa | null;
  onClose: () => void;
  onSaveAttendance: (nis: string, data: Partial<KehadiranSiswa>) => Promise<void>;
  onTriggerRfidSync?: () => Promise<void>;
  isSyncing?: boolean;
}

export const SiswaAttendanceModal: React.FC<SiswaAttendanceModalProps> = ({
  isOpen,
  siswa,
  onClose,
  onSaveAttendance,
  onTriggerRfidSync,
  isSyncing = false,
}) => {
  if (!isOpen || !siswa) return null;

  const initialKehadiran = siswa.kehadiran || {
    totalHariEfektif: 20,
    hadir: 19,
    izin: 1,
    sakit: 0,
    alpa: 0,
    persentaseHadir: 95,
    rfidCardNumber: siswa.rfidCard || `000${siswa.nis}01`,
    lastTapTime: '2026-09-19 06:55:00',
    lastDevice: 'Solution X107 (192.168.1.201)',
    statusKehadiran: 'BAIK',
  };

  const [totalHari, setTotalHari] = useState(initialKehadiran.totalHariEfektif || 20);
  const [hadir, setHadir] = useState(initialKehadiran.hadir);
  const [izin, setIzin] = useState(initialKehadiran.izin || 0);
  const [sakit, setSakit] = useState(initialKehadiran.sakit || 0);
  const [alpa, setAlpa] = useState(initialKehadiran.alpa || 0);
  const [rfidCard, setRfidCard] = useState(siswa.rfidCard || initialKehadiran.rfidCardNumber || '');
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // Dynamic percentage calculation
  const calculatedPct = totalHari > 0 ? Math.round((hadir / totalHari) * 100) : 0;
  const isKritis = calculatedPct < 70;
  const isPerhatian = calculatedPct >= 70 && calculatedPct < 80;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSaveAttendance(siswa.nis, {
        totalHariEfektif: totalHari,
        hadir,
        izin,
        sakit,
        alpa,
        persentaseHadir: calculatedPct,
        rfidCardNumber: rfidCard,
        statusKehadiran: isKritis ? 'KRITIS' : isPerhatian ? 'PERHATIAN' : 'BAIK',
      });
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 2500);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-sky-800 via-sky-700 to-teal-800 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shadow-inner">
              <Radio className="w-5 h-5 text-amber-300 animate-pulse" />
            </div>
            <div>
              <h3 className="font-extrabold text-sm tracking-tight">
                Data Kehadiran & RFID Terminal Solution X107
              </h3>
              <p className="text-xs text-sky-200">
                Profil Presensi Siswa & Sinkronisasi Kartu Akses Sekolah
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Student Summary Info Banner */}
        <div className="bg-sky-50/70 border-b border-sky-100 p-4 flex items-center justify-between gap-4 text-xs">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-slate-700 bg-white px-2 py-0.5 rounded border border-slate-200">
                NIS: {siswa.nis}
              </span>
              <span className="font-bold text-sky-900 text-sm">{siswa.nama}</span>
            </div>
            <div className="text-slate-500 mt-1 flex items-center gap-3 text-[11px]">
              <span>Kelas: <strong>{siswa.kelasNama}</strong></span>
              <span>&bull;</span>
              <span>Wali: {siswa.namaWali || '-'}</span>
              <span>&bull;</span>
              <span>VA: {siswa.noVA}</span>
            </div>
          </div>

          <div className="text-right">
            <div
              className={`text-2xl font-black font-mono leading-none ${
                isKritis ? 'text-rose-600' : isPerhatian ? 'text-amber-600' : 'text-emerald-600'
              }`}
            >
              {calculatedPct}%
            </div>
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase mt-1 ${
                isKritis
                  ? 'bg-rose-100 text-rose-800'
                  : isPerhatian
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-emerald-100 text-emerald-800'
              }`}
            >
              {isKritis ? 'Presensi Kritis (<70%)' : isPerhatian ? 'Perhatian (<80%)' : 'Kehadiran Baik'}
            </span>
          </div>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="p-5 space-y-4 text-xs">
          {/* RFID Hardware Identification */}
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
            <div className="flex items-center justify-between text-slate-700 font-bold">
              <span className="flex items-center gap-1.5">
                <Radio className="w-3.5 h-3.5 text-sky-600" />
                Terminal Mesin RFID Solution X107
              </span>
              <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                STATUS: TERHUBUNG
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Nomor Kartu RFID (UID 10-Digit)
                </label>
                <input
                  type="text"
                  value={rfidCard}
                  onChange={(e) => setRfidCard(e.target.value)}
                  placeholder="Contoh: 0002448101"
                  className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold text-slate-800 focus:ring-1 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Terakhir Tap Kartu
                </label>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-slate-600 font-mono text-[11px]">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span>{initialKehadiran.lastTapTime || 'Belum pernah tap'}</span>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-slate-400">
              Terminal: {initialKehadiran.lastDevice || 'Solution X107 (IP: 192.168.1.201, Port: 4370)'}
            </p>
          </div>

          {/* Attendance Stats Inputs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-slate-800 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-sky-700" />
                Rincian Rekap Presensi Bulan Berjalan
              </h4>
              <span className="text-[11px] text-slate-500 font-mono">
                Total Hari Efektif: <strong>{totalHari} Hari</strong>
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div className="p-2.5 bg-emerald-50/70 border border-emerald-200 rounded-xl">
                <label className="block text-[10px] font-bold text-emerald-800 uppercase mb-1">Hadir (Hari)</label>
                <input
                  type="number"
                  min="0"
                  max={totalHari}
                  value={hadir}
                  onChange={(e) => setHadir(Number(e.target.value))}
                  className="w-full px-2 py-1 bg-white border border-emerald-300 rounded font-mono font-bold text-emerald-900 text-sm focus:outline-none"
                  required
                />
              </div>

              <div className="p-2.5 bg-sky-50/70 border border-sky-200 rounded-xl">
                <label className="block text-[10px] font-bold text-sky-800 uppercase mb-1">Izin (Hari)</label>
                <input
                  type="number"
                  min="0"
                  max={totalHari}
                  value={izin}
                  onChange={(e) => setIzin(Number(e.target.value))}
                  className="w-full px-2 py-1 bg-white border border-sky-300 rounded font-mono font-bold text-sky-900 text-sm focus:outline-none"
                />
              </div>

              <div className="p-2.5 bg-amber-50/70 border border-amber-200 rounded-xl">
                <label className="block text-[10px] font-bold text-amber-800 uppercase mb-1">Sakit (Hari)</label>
                <input
                  type="number"
                  min="0"
                  max={totalHari}
                  value={sakit}
                  onChange={(e) => setSakit(Number(e.target.value))}
                  className="w-full px-2 py-1 bg-white border border-amber-300 rounded font-mono font-bold text-amber-900 text-sm focus:outline-none"
                />
              </div>

              <div className="p-2.5 bg-rose-50/70 border border-rose-200 rounded-xl">
                <label className="block text-[10px] font-bold text-rose-800 uppercase mb-1">Alpa (Hari)</label>
                <input
                  type="number"
                  min="0"
                  max={totalHari}
                  value={alpa}
                  onChange={(e) => setAlpa(Number(e.target.value))}
                  className="w-full px-2 py-1 bg-white border border-rose-300 rounded font-mono font-bold text-rose-900 text-sm focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Attendance Warning Notice */}
          {(isKritis || isPerhatian) && (
            <div className={`p-3 rounded-xl border flex items-start gap-2.5 ${
              isKritis ? 'bg-rose-50 border-rose-300 text-rose-900' : 'bg-amber-50 border-amber-300 text-amber-900'
            }`}>
              <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${isKritis ? 'text-rose-600' : 'text-amber-600'}`} />
              <div className="text-[11px] leading-relaxed">
                <strong>Status Peringatan Aktif:</strong> Siswa ini memiliki tingkat kehadiran <strong>{calculatedPct}%</strong> (&lt; 80%). Sistem loket kasir & dashboard secara otomatis menampilkan tanda peringatan khusus saat memproses tagihan maupun pembayaran.
              </div>
            </div>
          )}

          {savedSuccess && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Data presensi dan nomor kartu RFID berhasil disimpan!</span>
            </div>
          )}

          {/* Modal Footer Actions */}
          <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
            {onTriggerRfidSync && (
              <button
                type="button"
                onClick={onTriggerRfidSync}
                disabled={isSyncing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>{isSyncing ? 'Menghubungkan Mesin...' : 'Tarik dari Solution X107'}</span>
              </button>
            )}

            <div className="flex items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl font-medium transition cursor-pointer"
              >
                Tutup
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-xl font-semibold shadow-xs transition cursor-pointer disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isSaving ? 'Menyimpan...' : 'Simpan Presensi'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
